import { useMemo, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useOutsideClick } from '@/hooks'
import { PRODUCT_CATALOG } from '../lib/productCatalog'
import type { CostUnit } from '../model/types'
import styles from './ProductPicker.module.css'

interface ProductPickerProps {
  value: string
  /** unit приходит только при выборе из списка — своё название единицу не меняет. */
  onChange: (name: string, unit?: CostUnit) => void
  label?: string
}

/**
 * Поле продукта: свой выпадающий список вместо datalist.
 *
 * Нативный datalist по-разному отрисовывается в iOS и Android и не поддаётся
 * стилизации, поэтому список рисуем сами. Свободный ввод сохраняется: список
 * лишь подсказывает и подставляет единицу измерения.
 *
 * @param value - текущее название продукта
 * @param onChange - изменение названия; unit передаётся при выборе из списка
 * @param label - подпись поля для скринридеров
 */
export const ProductPicker = ({
  value,
  onChange,
  label = 'Продукт',
}: ProductPickerProps) => {
  const [isOpen, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(-1)
  const rootRef = useRef<HTMLDivElement>(null)

  useOutsideClick(rootRef, () => setOpen(false), isOpen)

  // Список не режем: без запроса нужно видеть весь каталог, а длину держит
  // скролл самого списка.
  const suggestions = useMemo(() => {
    const query = value.trim().toLowerCase()
    if (!query) return PRODUCT_CATALOG
    return PRODUCT_CATALOG.filter(p => p.name.toLowerCase().includes(query))
  }, [value])

  // Точное совпадение уже выбрано — подсказки только мешали бы.
  const isExactMatch = PRODUCT_CATALOG.some(
    p => p.name.toLowerCase() === value.trim().toLowerCase()
  )
  const showList = isOpen && suggestions.length > 0 && !isExactMatch

  const select = (name: string, unit: CostUnit) => {
    onChange(name, unit)
    setOpen(false)
    setHighlight(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false)
      return
    }
    if (!showList) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight(h => (h + 1) % suggestions.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(h => (h <= 0 ? suggestions.length - 1 : h - 1))
    } else if (e.key === 'Enter' && highlight >= 0) {
      e.preventDefault()
      const picked = suggestions[highlight]
      select(picked.name, picked.unit)
    }
  }

  return (
    <div className={styles.root} ref={rootRef}>
      <input
        className={styles.input}
        value={value}
        onChange={e => {
          onChange(e.target.value)
          setOpen(true)
          setHighlight(-1)
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Продукт — выберите или впишите свой"
        aria-label={label}
        aria-expanded={showList}
        aria-autocomplete="list"
        role="combobox"
        autoComplete="off"
      />

      <button
        type="button"
        className={styles.toggle}
        onClick={() => setOpen(o => !o)}
        aria-label={showList ? 'Скрыть список' : 'Показать список'}
        tabIndex={-1}
      >
        <ChevronDown size={16} aria-hidden />
      </button>

      {showList && (
        <ul className={styles.list} role="listbox">
          {suggestions.map((product, i) => (
            <li key={product.name}>
              <button
                type="button"
                role="option"
                aria-selected={i === highlight}
                className={`${styles.option} ${i === highlight ? styles.optionActive : ''}`}
                // mousedown, а не click: click срабатывает после blur, и список
                // успевает закрыться раньше, чем выбор долетит.
                onMouseDown={e => {
                  e.preventDefault()
                  select(product.name, product.unit)
                }}
              >
                <span className={styles.optionName}>{product.name}</span>
                <span className={styles.optionUnit}>{product.unit}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
